import { useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { useAtributo } from './useAtendimentos';
import { base64ToBlob, downloadBlob } from '@/lib/utils';

/**
 * Hook de download de atributo (RTF).
 * 1. Busca o bb_valor (base64) via /api/atendimentos/.../atributos/...
 * 2. Decodifica base64 → Blob .rtf
 * 3. Inicia download
 *
 * @param cdAtendimento id do atendimento
 * @param cdValor id do atributo (cd_valor)
 * @returns { download, isDownloading, error }
 */
export function useAtributoDownload(
  cdAtendimento: number,
  cdValor: number,
) {
  const atributo = useAtributo(cdAtendimento, cdValor);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const download = async (filename = 'historico-consulta.rtf') => {
    setIsDownloading(true);
    setError(null);
    try {
      // Usa o query cache se já tiver, senão busca
      let bbValor: string | null;
      if (atributo.data) {
        bbValor = atributo.data.bb_valor;
      } else {
        const fresh = await api.get<{ bb_valor: string | null }>(
          `/api/atendimentos/${cdAtendimento}/atributos/${cdValor}`,
        );
        bbValor = fresh.bb_valor;
      }

      if (!bbValor) {
        throw new Error('Este documento está vazio no Clinux.');
      }

      const blob = base64ToBlob(bbValor, 'application/rtf');
      downloadBlob(blob, filename);
    } catch (err) {
      const e =
        err instanceof ApiError
          ? err
          : err instanceof Error
            ? err
            : new Error(String(err));
      setError(e);
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    download,
    isDownloading: isDownloading || atributo.isFetching,
    error: error || (atributo.error as Error | null),
  };
}
